/**
 * The install runbook, which lives in the public agent repository.
 *
 * The platform's source is private and the runbook a founder pastes into a coding agent
 * has to stay anonymously readable, so `guides/` moved to `m8t-labs/ezra` — deliberately
 * OUTSIDE the paths the platform consumes as agent content, which is why moving it left
 * the release pin's contentSha untouched.
 *
 * Everything that names a guide goes through here. Fifteen call sites each holding their
 * own URL is the next sweep's debt, and one of them would be wrong.
 */

/** The repository that hosts the runbook. */
export const EZRA_REPO = "m8t-labs/ezra";

/** Human-facing base: what a founder opens when a command names a guide. */
export const EZRA_GUIDES_URL = `https://github.com/${EZRA_REPO}/blob/main/guides`;

/** A link to one guide, e.g. `guideUrl("uninstall.md")`. */
export function guideUrl(rel: string): string {
  return `${EZRA_GUIDES_URL}/${rel.replace(/^\/+/, "")}`;
}

/** Raw bytes of one guide, for the checks that hold generated blocks in lockstep. */
export function guideRawUrl(rel: string): string {
  return `https://raw.githubusercontent.com/${EZRA_REPO}/main/guides/${rel.replace(/^\/+/, "")}`;
}

/**
 * Fetch a guide from the canonical repository's default branch.
 *
 * `main`, not the release pin: `guides/` is outside the pin's consumed set by
 * construction, so a pinned ref would freeze at the last content bump and go stale the
 * first time the runbook is edited. Main is what a founder actually reads, and it is the
 * only honest thing to compare generated content against.
 *
 * Anonymous by design — no token, so this works in token-free required CI, and every run
 * incidentally re-verifies that the runbook is still publicly readable.
 *
 * There is no offline branch. A skipped network check is a dormant gate, and a gate that
 * goes quiet when it cannot reach its subject reports health it never checked.
 */
export async function fetchGuide(rel: string, fetchImpl: typeof fetch = fetch): Promise<string> {
  const url = guideRawUrl(rel);
  const res = await fetchImpl(url, { headers: { accept: "text/plain" } });
  if (!res.ok) {
    throw new Error(
      `could not read guides/${rel} from ${EZRA_REPO}: HTTP ${res.status.toString()} (${url}).\n` +
        "The runbook is public, so this is a real failure, not a permissions problem — " +
        "re-run if it was transient.",
    );
  }
  return await res.text();
}

/**
 * What to do when a generated block and the published runbook disagree.
 *
 * The order matters and is the whole point of spelling it out: the document is in another
 * repository now, so the fix is two pull requests and the doc one lands FIRST. A red gate
 * with no named path out is a red people learn to override.
 *
 * Step 1 used to name a command to regenerate the block with — and BOTH call sites named a
 * command that has never existed (`platform cascade-table`, `prereqs --render-table`). The
 * renderers are library functions with no CLI surface at all, so the one instruction this
 * gate gave you failed the moment you tried it: a guard whose remedy cannot be run is a
 * guard people learn to override, which is exactly what the paragraph above is about.
 * Guard remedy copy is code. It now points at something that genuinely exists — the
 * expected bytes are printed in the assertion's own failure diff — and names the function
 * that produced them so the block can be regenerated by hand if the diff is unwieldy.
 */
export function driftRemedy(guide: string, generatedBy: string): string {
  return [
    `guides/${guide} in ${EZRA_REPO} no longer matches what this repository generates.`,
    `  1. copy the expected block verbatim from this failure's diff (generated by ${generatedBy})`,
    `  2. open a PR against ${EZRA_REPO} with the updated guides/${guide} — that one merges FIRST`,
    "  3. re-run this check once it has merged",
  ].join("\n");
}
